// HeroHeadline — the main editorial headline plus the "People. Pride. Purpose."
// brand tagline. Words bloom in word-by-word with motion handling
// opacity + y, and a CSS keyframe handling the Fraunces optical-size +
// weight bloom (motion can tween font-variation-settings strings, but
// the simpler CSS-keyframes path is more reliable across browsers).

import { motion, useReducedMotion } from 'motion/react';

const TAGLINE_TOKENS = ['People', 'Pride', 'Purpose'];

interface Props {
  children: string;
}

export function HeroHeadline({ children }: Props) {
  const reduced = useReducedMotion();
  const headlineWords = children.trim().split(/\s+/);
  const taglineDelaySec = headlineWords.length * 0.09 + 0.2;

  if (reduced) {
    // Static render — no animation, final styles only.
    return (
      <div className="text-center">
        <h1
          className="font-display text-ink font-semibold leading-[1.05] tracking-tight"
          style={{ fontSize: 'clamp(2rem, 6vw, 4rem)' }}
        >
          {children}
        </h1>
        <p
          aria-label="People. Pride. Purpose."
          className="font-display text-accent mt-3 leading-tight tracking-wide"
          style={{ fontSize: 'clamp(1.1rem, 2.2vw, 1.5rem)', letterSpacing: '0.02em' }}
        >
          {TAGLINE_TOKENS.map((tok, i) => (
            <span key={tok} className={i > 0 ? 'ml-2' : ''}>
              {tok}
              <span className="text-ink/40">.</span>
            </span>
          ))}
        </p>
      </div>
    );
  }

  return (
    <div className="text-center">
      <style>{`
        @keyframes hero-bloom {
          from {
            font-variation-settings: "opsz" 14, "wght" 400;
          }
          to {
            font-variation-settings: "opsz" 144, "wght" 600;
          }
        }
        @keyframes hero-bloom-tag {
          from { font-variation-settings: "opsz" 14, "wght" 400; }
          to   { font-variation-settings: "opsz"  96, "wght" 500; }
        }
        .hero-bloom-word {
          display: inline-block;
          font-variation-settings: "opsz" 14, "wght" 400;
          animation: hero-bloom 700ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
          animation-delay: var(--bloom-delay, 0ms);
        }
        .hero-bloom-tag {
          display: inline-block;
          font-variation-settings: "opsz" 14, "wght" 400;
          animation: hero-bloom-tag 600ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
          animation-delay: var(--bloom-delay, 0ms);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-bloom-word,
          .hero-bloom-tag {
            animation: none !important;
            font-variation-settings: "opsz" 144, "wght" 600;
          }
        }
      `}</style>

      <h1
        className="font-display text-ink font-semibold leading-[1.05] tracking-tight"
        style={{ fontSize: 'clamp(2rem, 6vw, 4rem)' }}
      >
        {headlineWords.map((word, i) => (
          <motion.span
            key={`${word}-${i}`}
            className="hero-bloom-word inline-block"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.7,
              delay: i * 0.09,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={{ ['--bloom-delay' as string]: `${i * 90}ms` }}
          >
            {word}
            {i < headlineWords.length - 1 && ' '}
          </motion.span>
        ))}
      </h1>

      <p
        aria-label="People. Pride. Purpose."
        className="font-display text-accent mt-3 leading-tight"
        style={{
          fontSize: 'clamp(1.1rem, 2.2vw, 1.5rem)',
          letterSpacing: '0.02em',
        }}
      >
        {TAGLINE_TOKENS.map((token, i) => (
          <motion.span
            key={token}
            className={`hero-bloom-tag ${i > 0 ? 'ml-2' : ''}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: taglineDelaySec + i * 0.18,
              ease: [0.22, 1, 0.36, 1],
            }}
            style={
              {
                ['--bloom-delay' as string]: `${(taglineDelaySec + i * 0.18) * 1000}ms`,
              } as React.CSSProperties
            }
          >
            {token}
            <span className="text-ink/40">.</span>
          </motion.span>
        ))}
      </p>
    </div>
  );
}
